import { TextLink, Typography } from '@neo4j-ndl/react';
import { AcademicCapIconOutline, BookOpenIconOutline, GithubIcon } from '@neo4j-ndl/react/icons';
import { PLG_EVENTS } from '@nx/analytics-service';
import { APP_SCOPE } from '@nx/constants';
import { useActiveOrg, useAuraUser, useTrackEvent } from '@nx/state';
import type { ReactNode } from 'react';

const ResourceCard = ({
  title,
  icon,
  linkText,
  href,
  children,
  onClick,
}: {
  title: string;
  icon: ReactNode;
  linkText: string;
  href: string;
  children: ReactNode;
  onClick?: () => void;
}) => {
  return (
    <div className="bg-neutral-bg-weak rounded-1xl flex flex-col gap-4 p-4" title={title}>
      <div className="bg-neutral-bg-default flex min-h-[80px] items-center justify-center rounded-lg">
        <div className="size-8">{icon}</div>
      </div>
      <div className="flex h-full min-h-[152px] flex-col justify-between gap-4">
        <div className="flex flex-col gap-2">
          <h6>{title}</h6>
          <Typography variant="body-medium" className="text-neutral-text-weak tracking-normal">
            {children}
          </Typography>
        </div>
        <Typography variant="body-medium" className="tracking-normal">
          <TextLink href={href} type="external" htmlAttributes={{ onClick }}>
            {linkText}
          </TextLink>
        </Typography>
      </div>
    </div>
  );
};

const Resources = () => {
  const track = useTrackEvent();
  const { user } = useAuraUser();
  const org = useActiveOrg();
  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-col gap-2">
        <h5>Check out helpful resources</h5>
        <Typography variant="body-medium" className="tracking-normal">
          Find code samples, documentation, and courses to accelerate your Neo4j application building experience.
        </Typography>
      </div>
      <div className="@sm:flex-nowrap flex flex-wrap gap-5">
        <ResourceCard
          title="Example GitHub Repositories"
          icon={<GithubIcon width="100%" height="100%" />}
          linkText="Go to GitHub"
          href="https://github.com/neo4j-graph-examples"
          onClick={() => {
            track({
              event: PLG_EVENTS.DEVELOPER_HUB_LINK,
              scope: APP_SCOPE.aura,
              properties: {
                link: 'github-graph-examples',
                org_id: org.id,
                user_id: user?.id,
              },
            });
          }}
        >
          Dive into real-world examples of our tooling and services. Perfect for developers who learn by doing.
        </ResourceCard>
        <ResourceCard
          title="Documentation"
          icon={<BookOpenIconOutline className="size-8" />}
          linkText="Go to Documentation"
          href="https://neo4j.com/docs/"
          onClick={() => {
            track({
              event: PLG_EVENTS.DEVELOPER_HUB_LINK,
              scope: APP_SCOPE.aura,
              properties: {
                link: 'docs',
                org_id: org.id,
                user_id: user?.id,
              },
            });
          }}
        >
          Find detailed guides and API references covering every part of our platform. Ideal for hands-on builders and
          teams.
        </ResourceCard>
        <ResourceCard
          title="GraphAcademy"
          icon={<AcademicCapIconOutline className="size-8" />}
          linkText="Enroll in a course"
          href="https://graphacademy.neo4j.com/categories/"
          onClick={() => {
            track({
              event: PLG_EVENTS.DEVELOPER_HUB_LINK,
              scope: APP_SCOPE.aura,
              properties: {
                link: 'graph-academy',
                org_id: org.id,
                user_id: user?.id,
              },
            });
          }}
        >
          Level up your graph skills with interactive courses designed by experts. Learn at your own pace, anytime.
        </ResourceCard>
      </div>
    </div>
  );
};

export default Resources;
